import { useFormContext } from "react-hook-form";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { GooglePlacesAutocompleteInput } from "@/components/admin/google-places-autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { TextInput } from "@/components/admin/text-input";
import type { Contact } from "@/components/atomic-crm/types";
import { getContactDisplayName } from "@/modules/messages/messageContactUtils";
import { useOrgModules } from "@/modules/useOrgModules";

const requiredSubject = (value: unknown) =>
  typeof value === "string" && value.trim().length > 0
    ? undefined
    : "Subject is required";

/**
 * Ticket subject. Companies with Tickets → Job-site address subjects get
 * Google address suggestions; everyone else a plain text field.
 */
export const TicketSubjectInput = () => {
  const { setValue } = useFormContext();
  const { hasFeature } = useOrgModules();

  if (!hasFeature("tickets", "addressSubjects")) {
    return (
      <TextInput
        source="subject"
        label="Subject"
        helperText={false}
        validate={requiredSubject}
      />
    );
  }

  return (
    <GooglePlacesAutocompleteInput
      source="subject"
      label="Subject"
      mode="address"
      placeholder="Job site address or search on Google…"
      helperText="Pick a Google address suggestion, or type any subject."
      labelVariant="floating"
      validate={requiredSubject}
      onPlaceDetails={(details) => {
        const address =
          details.formattedAddress?.trim() || details.name?.trim();
        if (address) {
          setValue("subject", address, { shouldDirty: true });
        }
      }}
    />
  );
};

const contactOptionText = (contact: Contact) => getContactDisplayName(contact);

/** Contact search across every contact (no company first). */
export const TicketContactSearchInput = () => (
  <ReferenceInput source="contact_id" reference="contacts_summary">
    <AutocompleteInput
      label="Contact"
      labelVariant="floating"
      optionText={contactOptionText}
      inputText={contactOptionText}
      helperText={false}
      modal
      placeholder="Search contact…"
      filterToQuery={(searchText) => ({ q: searchText })}
    />
  </ReferenceInput>
);
